import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Check,
  KeyRound,
  DoorOpen,
  ShieldAlert,
  Shield,
  Mailbox,
  BadgeCheck,
} from "lucide-react";
import { COMPANY, SERVICES, SERVICE_IMAGE_1, SERVICE_IMAGE_2 } from "../lib/constants";
import useSeo, { breadcrumbJsonLd, SITE } from "../lib/useSeo";
import BrandMarquee from "../components/BrandMarquee";
import PricingGrid from "../components/PricingGrid";

const ICON_MAP = { KeyRound, DoorOpen, ShieldAlert, Shield, Mailbox, BadgeCheck };

export default function Services() {
  useSeo({
    title: "Nos services serrurerie — Tarifs transparents | Mon Serrurier Breton",
    description:
      "Tous nos services de serrurerie en Bretagne : ouverture de porte, changement de serrure et cylindre, mise en sécurité après effraction, blindage de porte, serrurerie boîte aux lettres. Tarifs publics, devis gratuit, intervention 30 min.",
    path: "/services",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Serrurerie & dépannage",
        provider: { "@id": `${SITE.url}/#business` },
        areaServed: [
          { "@type": "AdministrativeArea", name: "Côtes-d'Armor" },
          { "@type": "AdministrativeArea", name: "Ille-et-Vilaine" },
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Prestations serrurerie",
          itemListElement: SERVICES.map((s) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: s.title, description: s.short },
          })),
        },
      },
      breadcrumbJsonLd([
        { name: "Accueil", path: "/" },
        { name: "Nos services", path: "/services" },
      ]),
    ],
  });
  return (
    <div data-testid="page-services">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 space-y-5">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700">
              <span className="h-px w-8 bg-teal-700" /> Nos services
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
              Serrurerie, <span className="text-teal-600">sécurité</span> &
              dépannage <br />
              en Bretagne.
            </h1>
            <p className="text-slate-600 max-w-xl leading-relaxed">
              Intervention rapide, travail soigné et transparence totale sur les
              tarifs. Nous intervenons dans toutes les situations — du simple
              dépannage aux travaux de sécurité complets.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
              <img
                src={SERVICE_IMAGE_1}
                alt="Artisan Mon Serrurier Breton intervenant sur une porte d'entrée"
                className="w-full h-[420px] md:h-[460px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || Shield;
            return (
              <article
                key={s.id}
                data-testid={`service-item-${s.id}`}
                className="group bg-white border border-slate-200 rounded-3xl p-8 hover:border-teal-500 hover:shadow-xl transition-all"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors flex-shrink-0">
                    <Icon className="w-7 h-7" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="text-slate-300 font-display font-black text-lg">
                        0{i + 1}
                      </span>
                      <h2 className="font-display text-2xl font-bold text-slate-900">
                        {s.title}
                      </h2>
                    </div>
                    <p className="text-slate-600 leading-relaxed">
                      {s.details}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Checklist block */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-white border border-slate-200 rounded-3xl p-8 md:p-12">
          <div className="lg:col-span-7 space-y-5">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700">
              <span className="h-px w-8 bg-teal-700" /> Engagements
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Un travail d'artisan,
              <br />
              <span className="text-teal-600">sans mauvaise surprise.</span>
            </h2>
            <ul className="space-y-3 mt-4">
              {[
                "Devis gratuit, prix annoncé avant intervention",
                "Ouverture fine privilégiée pour préserver votre serrure",
                "Pièces de grandes marques (Picard, Vachette, Bricard…)",
                "Facture détaillée — remboursement assurance possible",
                "Intervention en 30 minutes en moyenne",
                "Garantie sur la pose et les pièces installées",
              ].map((it) => (
                <li
                  key={it}
                  className="flex items-start gap-3 text-slate-700"
                >
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-teal-600 text-white flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  <span className="leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-slate-200">
              <img
                src={SERVICE_IMAGE_2}
                alt="Artisan serrurier au travail"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee />

      {/* Pricing grid */}
      <PricingGrid />


      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="rounded-3xl bg-slate-900 text-white p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-teal-500/20 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
              Un projet de sécurité
              <br />
              ou un dépannage urgent ?
            </h2>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a
                data-testid="services-cta-call"
                href={`tel:${COMPANY.phoneDigits}`}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-6 py-4 text-lg shadow-xl shadow-orange-900/30"
              >
                <Phone className="w-5 h-5" /> {COMPANY.phone}
              </a>
              <Link
                data-testid="services-cta-contact"
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold rounded-full px-6 py-4"
              >
                Demander un devis <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
